import React from 'react';
import { useTranslation } from 'react-i18next';
import { AutoHideScrollArea } from '@/components/AutoHideScrollArea';
import { ArrowLeft } from '@/components/Icons/ArrowLeft';
import { Close } from '@/components/Icons/Close';
import { Modal } from '@/components/Modal';
import { useScrollFadeProgress } from '@/hooks/useScrollFadeProgress';
import {
  GALLERY_PREVIEW_THUMBNAIL_SIZE,
  GalleryThumbnail,
} from '@/pages/DetailedNews/GalleryThumbnail';
import { ModalSize } from '@/types/components';
import type { GalleryResponse } from '@/types/gallery';

interface Props {
  isAnimated: boolean;
  gallery: GalleryResponse;
  onSelect: (index: number) => void;
  onClose: () => void;
}

export const GalleryPreviewModal: React.FC<Props> = ({
  isAnimated,
  gallery,
  onSelect,
  onClose,
}) => {
  const { t } = useTranslation();
  const { progress, handleScroll } = useScrollFadeProgress();

  const title = t('home.newsDetail.galleryTitle');

  return (
    <Modal
      isOpen
      size={ModalSize.XLarge}
      className={`md:h-[min(788px,calc(100svh-80px))] md:w-[min(1053px,calc(100vw-200px))] ${isAnimated ? '' : 'max-md:data-[state=open]:animate-none md:data-[state=open]:animate-none'}`}
      label={title}
      onClose={onClose}
    >
      <div className="flex h-full flex-col">
        <div className="relative flex shrink-0 items-center gap-4 bg-background px-4 pt-[calc(16px+env(safe-area-inset-top))] pb-3 md:items-start md:justify-between md:gap-3 md:px-5 md:pt-5 md:pb-6">
          <button
            type="button"
            aria-label={t('global.back')}
            onClick={onClose}
            className="flex size-6 shrink-0 cursor-pointer items-center justify-center text-foreground md:hidden"
          >
            <ArrowLeft />
          </button>
          <h2
            style={{ opacity: progress }}
            className="text-callout-16 absolute left-1/2 max-w-[55%] -translate-x-1/2 truncate text-foreground transition-opacity duration-75 md:hidden"
          >
            {title}
          </h2>

          <div className="hidden min-w-0 flex-col gap-1 md:flex">
            <h2 className="text-headline-20 truncate text-foreground">
              {title}
            </h2>
            {gallery.title && (
              <p className="text-body-14 truncate text-subtle-foreground">
                {gallery.title}
              </p>
            )}
          </div>
          <button
            type="button"
            aria-label={t('global.close')}
            onClick={onClose}
            className="hidden size-6 shrink-0 cursor-pointer items-center justify-center text-foreground md:flex"
          >
            <Close />
          </button>
        </div>

        <AutoHideScrollArea
          onScroll={handleScroll}
          className="flex min-h-0 flex-1 flex-col"
        >
          <div
            style={{ opacity: 1 - progress }}
            className="flex flex-col gap-1 px-4 pb-3 transition-opacity duration-75 md:hidden"
          >
            <h2 className="text-title-30 text-foreground">{title}</h2>
            {gallery.title && (
              <p className="text-body-14 text-subtle-foreground">
                {gallery.title}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2 px-4 pb-[env(safe-area-inset-bottom)] md:grid-cols-5 md:px-5">
            {gallery.images.map((image, index) => (
              <GalleryThumbnail
                key={image.id}
                size={GALLERY_PREVIEW_THUMBNAIL_SIZE}
                className="aspect-[4/3] w-full"
                image={image}
                onClick={() => onSelect(index)}
              />
            ))}
          </div>
        </AutoHideScrollArea>
      </div>
    </Modal>
  );
};
